import React, { useState, useCallback, useEffect } from 'react';
import { isSessionCompleteMessage, logFilteredMessage } from '../utils/messageFilter';
import './ToastNotification.css';

export interface ToastNotification {
  id: string;
  type: 'OverdueReminder' | 'QueueUpdate' | 'SessionComplete' | 'Warning' | 'General';
  message: string;
  timestamp: Date;
  duration?: number; // Duration in seconds
  severity?: 'error' | 'warning' | 'info' | 'success';
  icon?: string;
  metadata?: Record<string, any>;
}

interface ToastNotificationProps {
  notification: ToastNotification;
  onClose: (id: string) => void;
}

interface ToastContainerProps {
  notifications: ToastNotification[];
  onClose: (id: string) => void;
  maxNotifications?: number;
}

/**
 * Individual toast notification component
 */
export const ToastNotificationComponent: React.FC<ToastNotificationProps> = ({ 
  notification, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  const duration = notification.duration || 30; // Default 30 seconds
  const severity = notification.severity || 'info';

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onClose(notification.id), 300); // Wait for animation
  }, [notification.id, onClose]);

  // Auto-close timer with progress bar
  useEffect(() => {
    setIsVisible(true);
    
    const startTime = Date.now();
    const endTime = startTime + (duration * 1000);
    
    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const progressPercent = (remaining / (duration * 1000)) * 100;
      
      setProgress(progressPercent);
      
      if (remaining <= 0) {
        handleClose();
      }
    };
    
    // Update progress every 100ms for smooth animation
    const progressInterval = setInterval(updateProgress, 100);
    
    return () => clearInterval(progressInterval);
  }, [duration, handleClose]);

  const getSeverityClass = () => `toast-${severity}`;
  
  const formatTimestamp = (timestamp: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(timestamp);
  };

  return (
    <div 
      className={`toast-notification ${getSeverityClass()} ${isVisible ? 'toast-visible' : 'toast-hidden'}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {/* Header */}
      <div className="toast-header">
        <div className="toast-icon-title">
          <span className="toast-icon" aria-hidden="true">
            {notification.icon}
          </span>
          <span className="toast-title">
            {notification.type === 'OverdueReminder' && 'Charging Overdue'}
            {notification.type === 'QueueUpdate' && 'Queue Update'}
            {notification.type === 'SessionComplete' && 'Session Complete'}
            {notification.type === 'Warning' && 'Warning'}
            {notification.type === 'General' && 'Notification'}
          </span>
        </div>
        
        <div className="toast-controls">
          <span className="toast-timestamp">
            {formatTimestamp(notification.timestamp)}
          </span>
          <button 
            className="toast-close-btn"
            onClick={handleClose}
            aria-label="Close notification"
            type="button"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Message Content */}
      <div className="toast-content">
        <div 
          className="toast-message"
          dangerouslySetInnerHTML={{ 
            __html: notification.message.replace(/\n/g, '<br>') 
          }}
        />
        
        {/* Additional metadata for overdue reminders */}
        {notification.type === 'OverdueReminder' && notification.metadata && (
          <div className="toast-details">
            {notification.metadata.OverdueMinutes && (
              <span className="overdue-time">
                Overdue by {notification.metadata.OverdueMinutes} minutes
              </span>
            )}
            {notification.metadata.SessionId && (
              <span className="session-id">
                Session: {notification.metadata.SessionId.slice(-8)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Progress Bar */}
      <div className="toast-progress-container">
        <div 
          className="toast-progress-bar"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Toast container component that manages multiple notifications
 */
export const ToastContainer: React.FC<ToastContainerProps> = ({ 
  notifications, 
  onClose,
  maxNotifications = 5 
}) => {
  // Limit the number of visible notifications
  const visibleNotifications = notifications.slice(0, maxNotifications);

  return (
    <div className="toast-container" aria-live="polite">
      {visibleNotifications.map((notification) => (
        <ToastNotificationComponent
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
    </div>
  );
};

/**
 * Hook for managing toast notifications
 */
export const useToastNotifications = () => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const addNotification = useCallback((notification: Omit<ToastNotification, 'id' | 'timestamp'>) => {
    console.log('🍞 Adding toast notification:', notification);
    
    // Filter session complete messages at toast level as additional safety
    if (isSessionCompleteMessage({ data: { message: notification.message, type: notification.type } })) {
      logFilteredMessage({ data: { message: notification.message, type: notification.type } }, 'Session complete toast blocked at useToastNotifications level');
      return '';
    }
    
    const newNotification: ToastNotification = {
      ...notification,
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };

    console.log('🍞 Created notification with ID:', newNotification.id);
    
    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      console.log('🍞 Updated notifications list, count:', updated.length);
      return updated;
    });
    
    return newNotification.id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const addOverdueReminder = useCallback((message: string, metadata?: Record<string, any>) => {
    console.log('🚨 Adding overdue reminder toast:', { message, metadata });
    return addNotification({
      type: 'OverdueReminder',
      message,
      duration: 60, // 1 minute for overdue reminders
      severity: 'error',
      icon: '🚨',
      metadata
    });
  }, [addNotification]);

  const addQueueUpdate = useCallback((message: string, metadata?: Record<string, any>) => {
    return addNotification({
      type: 'QueueUpdate',
      message,
      duration: 30,
      severity: 'info',
      icon: '📋',
      metadata
    });
  }, [addNotification]);

  const addSuccessNotification = useCallback((message: string, metadata?: Record<string, any>) => {
    return addNotification({
      type: 'SessionComplete',
      message,
      duration: 15,
      severity: 'success',
      icon: '✅',
      metadata
    });
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    addOverdueReminder,
    addQueueUpdate,
    addSuccessNotification
  };
};